import { useEffect, useState } from "react";
import { Quote, GraduationCap, UserRound, Facebook, Instagram, Youtube } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import WhatsAppIcon from "../components/icons";
import { api, resolveMedia } from "../lib/api";
import { useLang } from "../i18n";
import { SpecialCta } from "../components/sections/CtaSections";

export default function Founder() {
  const { pick } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/founders").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main>
      <Seo
        title="संस्थापक — Anansha Welfare Society"
        description="Anansha Welfare Society की संस्थापक/सचिव डॉ संगीता सिंह — विजन, संदेश और सामाजिक योगदान के बारे में जानें।"
        path="/founder"
        breadcrumb={["संस्थापक"]}
      />
      <PageHero
        overline={{ hi: "हमारी प्रेरणा", en: "Our Inspiration" }}
        title={{ hi: "संस्थापक", en: "Our Founder" }}
        description={{ hi: "जिनके विजन और प्रयास से अंशा वेलफेयर सोसाइटी की शुरुआत हुई।", en: "The vision and effort behind Anansha Welfare Society." }}
        testid="founder-page-heading"
      />
      <section className="py-20 bg-ngo-cream min-h-[40vh]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-center text-ngo-ink/50 py-16">...</p>
          ) : items.length === 0 ? (
            <p data-testid="founder-empty" className="text-center text-ngo-ink/60 py-20">{pick({ hi: "संस्थापक की जानकारी जल्द यहां उपलब्ध होगी।", en: "Founder information will be available here soon." })}</p>
          ) : (
            items.map((f, idx) => (
              <div key={f.id} data-testid={`founder-profile-${idx}`} className="grid lg:grid-cols-[380px_1fr] gap-12 items-start mb-16">
                <Reveal>
                  <div className="relative">
                    <div className="rounded-[2rem] overflow-hidden border border-ngo-deep/10 shadow-[0_24px_70px_rgba(23,107,58,0.16)] bg-ngo-mint aspect-[4/5] flex items-center justify-center">
                      {f.photo ? (
                        <img src={resolveMedia(f.photo)} alt={`${f.name} — ${f.designation}`} className="w-full h-full object-cover" />
                      ) : (
                        <UserRound size={120} className="text-ngo-deep/30" />
                      )}
                    </div>
                    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-ngo-deep text-white rounded-2xl px-6 py-3 shadow-lg text-center whitespace-nowrap">
                      <p className="font-heading font-bold">{f.name}</p>
                      <p className="text-xs text-ngo-gold">{f.designation}</p>
                    </div>
                  </div>
                </Reveal>
                <div>
                  <Reveal i={1}>
                    <h2 className="font-heading font-black text-3xl sm:text-4xl text-ngo-ink tracking-tight">{f.name}</h2>
                    <p className="mt-2 text-ngo-leaf font-semibold">{f.designation}</p>
                  </Reveal>
                  {f.intro && (
                    <Reveal i={2}>
                      <p className="mt-5 text-base md:text-lg text-ngo-ink/75 leading-relaxed">{f.intro}</p>
                    </Reveal>
                  )}
                  {f.message && (
                    <Reveal i={3}>
                      <blockquote data-testid="founder-message" className="mt-7 bg-ngo-forest text-ngo-cream rounded-3xl p-7 relative overflow-hidden">
                        <Quote size={40} className="absolute -top-1 -left-1 text-ngo-gold/25" />
                        <p className="relative leading-relaxed text-ngo-mint/90 italic">{f.message}</p>
                      </blockquote>
                    </Reveal>
                  )}
                  {f.bio && (
                    <Reveal i={4}>
                      <div className="mt-7">
                        <h3 className="font-heading font-bold text-xl text-ngo-ink mb-3">{pick({ hi: "जीवनी एवं योगदान", en: "Biography & Contributions" })}</h3>
                        <p className="text-base text-ngo-ink/70 leading-relaxed whitespace-pre-line">{f.bio}</p>
                      </div>
                    </Reveal>
                  )}
                  {f.education && (
                    <Reveal i={5}>
                      <div className="mt-6 flex items-start gap-3 bg-ngo-mint rounded-2xl px-5 py-4">
                        <GraduationCap size={20} className="text-ngo-deep shrink-0 mt-0.5" />
                        <p className="text-sm text-ngo-ink/75"><span className="font-bold text-ngo-ink">{pick({ hi: "शिक्षा / पृष्ठभूमि: ", en: "Education / Background: " })}</span>{f.education}</p>
                      </div>
                    </Reveal>
                  )}
                  {(f.social?.facebook || f.social?.instagram || f.social?.youtube || f.social?.whatsapp) && (
                    <Reveal i={6}>
                      <div className="mt-7 flex gap-3">
                        {f.social.facebook && <a href={f.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-deep hover:text-white transition-colors duration-200"><Facebook size={17} /></a>}
                        {f.social.instagram && <a href={f.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-deep hover:text-white transition-colors duration-200"><Instagram size={17} /></a>}
                        {f.social.youtube && <a href={f.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-10 h-10 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-deep hover:text-white transition-colors duration-200"><Youtube size={17} /></a>}
                        {f.social.whatsapp && <a href={`https://wa.me/${f.social.whatsapp}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-deep hover:text-white transition-colors duration-200"><WhatsAppIcon size={17} /></a>}
                      </div>
                    </Reveal>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
      <SpecialCta />
    </main>
  );
}
